import React, { useMemo, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Grid } from '~/components';
import { asyncState, useAuth } from '~/hooks';
import { ActionMenu, DateCell, SubmittedCell, OwnerCell, mapOwner, FirmName, StatusCell } from './gridCells';
import { useOrderHistory } from './context';
import { get } from 'lodash';
import { sortDates, sortStrings } from '~/util/sorters';
import RevisionsModal from '../HomePage/LawFirm/RevisionsModal';

const headerClassName = 'bg-gray-50 p-4 border-b text-left';
const props = {
    className: 'p-4 text-sm border-b'
};

const OrdersGrid = ({ pageSize, currentPage, handlePageChange, handlePageSizeChange }) => {
    const { isLawFirm, isTryon } = useAuth();

    const { gridData, filteredRows, firmData } = useOrderHistory();

    const isLoading = gridData.status == asyncState.PENDING;
    const rows = Array.isArray(filteredRows) ? filteredRows : [];

    const [activeTitleOpinionId, setActiveTitleOpinionId] = useState(null);
    const [revisionsOpen, setRevisionsOpen] = useState(false);
    const showRevisionsModal = () => setRevisionsOpen(true);

    const firms = useMemo(
        () => firmData.status == asyncState.SUCCESS ? firmData.value : [],
        [firmData.status]);

    let columns = [
        {
            title: isTryon ? 'File No.' : 'Your File No.',
            field: 'fileNumber',
            headerClassName,
            props
        },
        {
            title: isTryon ? 'Order No.' : 'Tryon File No.',
            field: 'orderNumber',
            headerClassName,
            props
        },
        {
            title: 'Submitted On',
            cell: DateCell('submitted'),
            sorter: (a, b) => sortDates(a.submitted, b.submitted),
            headerClassName,
            props
        },
        {
            title: 'Submitted By',
            cell: SubmittedCell,
            sorter: (a, b) => {
                const fname = 'submittedBy.firstName';
                const lname = 'submittedBy.lastName';

                const names = {
                    a: `${get(a, fname, '')} ${get(a, lname, '')}`.toLowerCase(),
                    b: `${get(b, fname, '')} ${get(b, lname, '')}`.toLowerCase()
                };

                if (names.a < names.b)
                    return -1;
                if (names.a > names.b)
                    return 1;
                return 0;
            },
            headerClassName,
            props
        },
        {
            title: 'Street Address',
            field: 'streetAddress',
            headerClassName,
            props
        },
        {
            title: 'Current Owner',
            cell: OwnerCell,
            sorter: sortByOwners,
            headerClassName,
            props
        },
        {
            title: 'Firm Name',
            cell: FirmName(firms),
            sorter: sortByFirm(firms),
            headerClassName,
            props
        },
        {
            title: 'Form Status',
            cell: StatusCell,
            sorter: (a, b) => a.statusId - b.statusId,
            width: 196,
            headerClassName,
            props
        },
        {
            title: '',
            key: 'actions',
            canSort: false,
            cell: props => (
                <ActionMenu
                    setActiveTitleOpinionId={setActiveTitleOpinionId}
                    showRevisionsModal={showRevisionsModal}
                    {...props}
                />
            ),
            headerClassName,
            props
        }
    ];

    if (isLawFirm) {
        columns = columns.filter(c => c.title != 'Firm Name');
    }

    return (
        <div className='my-4'>
            {isLoading ? (
                <AiOutlineLoading3Quarters className='text-primary-50 animate-spin mt-10' size='2rem' />
            ) : (
                <Grid
                    usePagination
                    data={rows}
                    page={currentPage}
                    maxRows={gridData.total}
                    columns={columns}
                    className='w-full rounded-md'
                    tableClassName='mb-2'
                    events={{
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange
                    }}
                    initialState={{
                        defaultPageSize: pageSize
                    }}
                />
            )}
            <RevisionsModal
                isOpen={revisionsOpen}
                setIsOpen={setRevisionsOpen}
                activeTitleOpinionId={activeTitleOpinionId}
            />
        </div>
    );
};

export default OrdersGrid;

function sortByOwners(a, b) {
    const aOwners = Array.isArray(a.owners)
        ? a.owners.map(mapOwner).filter(o => typeof o == 'string' && o.trim().length > 0)
        : [];

    const bOwners = Array.isArray(a.owners)
        ? b.owners.map(mapOwner).filter(o => typeof o == 'string' && o.trim().length > 0)
        : [];

    if (aOwners.length < 1) {
        return bOwners.length < 1 ? 0 : 1;
    }

    if (bOwners.length < 1) {
        return -1;
    }

    aOwners.sort(sortStrings);
    bOwners.sort(sortStrings);

    if (aOwners[0].toLowerCase() < bOwners[0].toLowerCase())
        return -1;
    if (aOwners[0].toLowerCase() > bOwners[0].toLowerCase())
        return 1;
    return 0;
}

function sortByFirm(firms) {
    return (a, b) => {
        const aFirm = firms.find(f => f.Id == a.firmId);
        const bFirm = firms.find(f => f.Id == b.firmId);

        return sortStrings(aFirm?.Name, bFirm?.Name);
    };
}